/**
 * Libris brand assets.
 *
 * The mark is an open book read as an "L": a solid blue cover whose top edge
 * falls away to the right, and a pale page tucked into the opening. Geometry is
 * measured from assets/branding/libris-icon.png and drawn once here, then
 * reused for the favicon and the static SVG files in public/, so there is a
 * single source of truth.
 */

/** Cover ("L") in a 64x64 box, occupying x 14.29..49.71 and y 10..54. */
export const MARK_PATH =
  'M16.52 10.11L26.18 17.63A4.12 4.12 0 0 1 27.76 20.88V39.72A3.21 3.21 0 0 0 30.98 42.94H44.34A5.38 5.38 0 0 1 49.71 48.31V54H23.65A9.37 9.37 0 0 1 14.29 44.63V11.2A1.39 1.39 0 0 1 16.52 10.11Z'

/** Page panel, same 64x64 box. Sits inside the cover's opening. */
export const PAGE_PATH =
  'M32.81 20.34L40.84 15.43A2.68 2.68 0 0 1 44.91 17.72V39.71H33.19A2.24 2.24 0 0 1 30.95 37.48V23.64A3.88 3.88 0 0 1 32.81 20.34Z'

export const BRAND_BLUE = '#0B5CFF'
export const BRAND_PAGE = '#DAE8FD'

export function BrandMark({ size = 30, className }: { size?: number; className?: string }) {
  return (
    <svg
      className={className ? `brand-mark ${className}` : 'brand-mark'}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label="Libris"
      focusable="false"
    >
      <path fill="currentColor" d={MARK_PATH} />
      <path fill="var(--brand-page)" d={PAGE_PATH} />
    </svg>
  )
}

/**
 * The "libris" wordmark, drawn as outlines rather than live text so it renders
 * identically on every machine and keeps its blue i-dots. Traced from
 * assets/branding/libris-wordmark.png into the lockup's coordinate system:
 * baseline y=58.6, x-height top y=25, ascender top y=10, stem weight 7.9.
 * Letters inherit currentColor; the dots stay brand blue.
 */
const WORD_PATH =
  /* l */ 'M63.87 10.07L63.94 58.77L71.55 58.73L71.7 10.32L64.2 10.08L63.87 10.07Z' +
  /* i */ 'M82.04 25.1L81.84 58.64L89.42 58.76L89.64 25.25L82.48 25.11L82.04 25.1Z' +
  /* b */ 'M99.67 10.09L99.62 58.73L107.2 58.63L107.32 55.87L110.91 58.19C116 60.64 122.3 59.81 126.95 56.83C138.05 49.72 136.33 29.6 123.49 25.37C119.2 23.96 114.32 24.1 110.28 26.22L107.36 28.15L107.24 10.16L100.06 10.09L99.67 10.09Z' +
  /* b bowl */ 'M115 31.38C128.3 29.02 131.26 50.74 117.98 52.82C104.86 54.87 101.74 34.05 115 31.38Z' +
  /* r */ 'M148.59 29.51L148.35 25.21L140.78 25.17L140.85 58.74L148.38 58.57C148.86 50.98 145.53 36.09 154.47 32.55C156.54 31.74 159.01 31.57 161.14 32.25L161.32 24.73C157.28 22.73 150.84 26.1 148.59 29.51Z' +
  /* i */ 'M168.07 25.11L168 58.69L175.58 58.73L175.62 25.16L168.43 25.11L168.07 25.11Z' +
  /* s */ 'M186.41 49.76L183.12 55.68C189.76 61.45 206.82 61.8 209.66 51.7C210.11 50.08 210.26 48.19 209.85 46.54L209.14 44.69C205.88 38.23 198.16 40.54 192.98 37.34C189.44 35.15 192.35 31.51 195.38 31.01L200.55 31.33L206.29 33.97L209.33 28.36L206.83 26.56C199.84 23.07 186.78 23.09 184.04 32.19C183.44 34.18 183.46 36.55 184.16 38.5C187.23 46.97 197.5 43.79 201.47 47.44C202.19 48.09 202.47 49.06 202.24 49.99C201.35 53.67 195.63 53.53 192.8 52.73C190.78 52.15 187.2 49.93 186.41 49.76Z'

/**
 * Full lockup: mark plus wordmark at the proportions of the source artwork.
 * The mark is scaled from the 64-box to 56 units tall and pinned to the left.
 */
export function BrandLockup({ height = 26, className }: { height?: number; className?: string }) {
  return (
    <svg
      className={className ? `brand-lockup ${className}` : 'brand-lockup'}
      height={height}
      viewBox="0 0 210 64"
      role="img"
      aria-label="Libris"
      focusable="false"
    >
      <g className="lockup-mark" transform="translate(0 4) scale(1.27273) translate(-14.29 -10)">
        <path fill="var(--brand-mark)" d={MARK_PATH} />
        <path fill="var(--brand-page)" d={PAGE_PATH} />
      </g>
      <g className="lockup-word">
        <path fill="currentColor" d={WORD_PATH} />
        <g fill="var(--brand-mark)">
          <circle cx="85.59" cy="13.35" r="4.69" />
          <circle cx="171.73" cy="13.35" r="4.69" />
        </g>
      </g>
    </svg>
  )
}
